import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { UserAvatar } from '@/components/UserAvatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUserProfiles } from '@/hooks/useUserProfiles';

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  budget: number | null;
  pricing_type: string;
  created_at: string;
  service_category_id: string | null;
  user_id: string;
  image_url: string | null;
}

interface RequestListProps {
  categoryId?: string;
  searchQuery?: string;
  priceRange?: string;
  deliveryMethod?: string;
}

// Category icons map matching DiscoverServices
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Coding": Briefcase,
  "Design": Briefcase,
  "Writing": Briefcase,
  "Marketing and Sales": Briefcase,
  "Gaming": Briefcase,
  "Tutoring": Briefcase,
  "Photography": Briefcase,
  "Music": Briefcase,
  "Language": Briefcase,
  "Chores": Briefcase,
  "Fashion": Briefcase,
  "Business": Briefcase,
  "Arts": Briefcase,
  "Engineering and CAD": Briefcase,
  "Other": Briefcase
};

// Category colors matching DiscoverServices
const categoryColors: Record<string, string> = {
  "Coding": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Gaming": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Chores": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

export const RequestList: React.FC<RequestListProps> = ({ 
  categoryId, 
  searchQuery = "", 
  priceRange = "", 
  deliveryMethod = "" 
}) => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<any[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const { getUserProfile } = useUserProfiles();

  const highlightId = searchParams.get('highlight');

  const toggleExpanded = (id: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchRequestsAndCategories();
  }, [categoryId, searchQuery, priceRange, deliveryMethod]);

  useEffect(() => {
    // Scroll to highlighted request after data loads
    if (highlightId && requests.length > 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`request-${highlightId}`);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Add highlight animation
          element.classList.add('animate-pulse');
          setTimeout(() => {
            element.classList.remove('animate-pulse');
          }, 3000);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightId, requests]);

  const fetchRequestsAndCategories = async () => {
    setIsLoading(true);
    
    try {
      let query = supabase
        .from('service_requests')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply category filter
      if (categoryId) {
        query = query.eq('service_category_id', categoryId);
      }

      // Apply search query filter
      if (searchQuery && searchQuery.trim() !== '') {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      // Apply price range filter
      if (priceRange && priceRange !== 'any') {
        if (priceRange === "100+") {
          query = query.gte('budget', 100);
        } else {
          const [min, max] = priceRange.split("-").map(Number);
          if (!isNaN(min) && !isNaN(max)) {
            query = query.gte('budget', min).lte('budget', max);
          }
        }
      }

      const { data: requestsData, error: requestsError } = await query;

      console.log('Service requests query result:', { requestsData, requestsError });

      if (requestsError) {
        console.error('Error fetching requests:', requestsError);
        toast({
          title: 'Error',
          description: 'Failed to load service requests',
          variant: 'destructive',
        });
        return;
      }

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('service_categories')
        .select('id, name');

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
      }

      setRequests(requestsData || []);
      setCategories(categoriesData || []);

      // Load user profiles
      if (requestsData && requestsData.length > 0) {
        const userIds = [...new Set(requestsData.map(request => request.user_id))];
        const profiles: Record<string, any> = {};
        
        for (const userId of userIds) {
          try {
            const profile = await getUserProfile(userId);
            profiles[userId] = profile;
          } catch (error) {
            console.error(`Error loading profile for ${userId}:`, error);
          }
        }
        
        setUserProfiles(profiles);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while loading requests',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatBudget = (budget: number | null) => {
    if (!budget) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(budget);
  };

  const getCategoryNameById = (id: string | null) => {
    if (!id) return "Uncategorized";
    const category = categories.find(cat => cat.id === id);
    return category ? category.name : "Uncategorized";
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(8)].map((_, index) => (
          <Card key={index} className="animate-pulse overflow-hidden">
            <div className="aspect-[4/3] bg-gray-200"></div>
            <CardContent className="p-4">
              <div className="flex items-center mb-3">
                <div className="w-6 h-6 rounded-full bg-gray-200 mr-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="space-y-2 mb-3">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
              <div className="flex justify-between items-end">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">
          {categoryId || searchQuery || priceRange || deliveryMethod
            ? 'No service requests found matching your criteria.' 
            : 'No service requests found.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {requests.map((request) => {
        const categoryName = getCategoryNameById(request.service_category_id);
        const userProfile = userProfiles[request.user_id] || { 
          name: "Unknown User", 
          avatarInitials: "UN",
          avatarUrl: undefined 
        };
        const IconComponent = categoryIcons[categoryName] || Briefcase;
        const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
        const isExpanded = expandedCards.has(request.id);
        
        return (
          <Card 
            key={request.id} 
            id={`request-${request.id}`}
            className={`overflow-hidden hover:shadow-md transition-shadow relative border-2 border-blue-200 ${
              highlightId === request.id ? 'ring-2 ring-green-500 bg-green-50' : ''
            }`}
          >
            {/* Request Badge */}
            <div className="absolute top-2 left-2 z-10">
              <Badge variant="secondary" className="bg-blue-500 text-white text-xs font-medium px-2 py-1">
                REQUEST
              </Badge>
            </div>

            {/* Category Icon/Image Area - matching service card design with aspect-[4/3] */}
            <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
              {request.image_url ? (
                <>
                  <img 
                    src={request.image_url} 
                    alt={request.title} 
                    className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800" 
                  />
                  <div className="absolute top-2 right-2">
                    <div className={`${colorClass} p-1.5 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className={`${colorClass} p-2 rounded-full`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                </div>
              )}
            </div>
            
            <CardContent className="p-4">
              {/* User Profile Section - matching service card design */}
              <Link to={`/profile/${request.user_id}`} className="flex items-center mb-3 hover:text-anyskill-blue">
                <UserAvatar
                  userId={request.user_id}
                  avatarUrl={userProfile.avatarUrl}
                  fallback={userProfile.avatarInitials}
                  size="sm"
                  showLevel={true}
                  clickable={false}
                  className="mr-2"
                />
                <span className="text-sm text-muted-foreground hover:text-anyskill-blue truncate">{userProfile.name}</span>
              </Link>
              
              {/* Title - matching service card design */}
              <CardTitle className="text-base mb-3 line-clamp-2 leading-tight dark:text-white">{request.title}</CardTitle>
              
              {/* Description - expandable like service cards */}
              <p 
                className={`text-sm text-muted-foreground mb-3 ${isExpanded ? '' : 'line-clamp-3'} cursor-pointer hover:text-foreground transition-colors`}
                onClick={() => toggleExpanded(request.id)}
                title="Click to expand/collapse"
              >
                {request.description}
              </p>
              
              {/* Expand/Collapse indicator */}
              {request.description && request.description.length > 150 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="p-0 h-auto text-xs text-muted-foreground hover:text-foreground mb-3"
                  onClick={() => toggleExpanded(request.id)}
                >
                  {isExpanded ? (
                    <>Show less <ChevronUp className="h-3 w-3 ml-1" /></>
                  ) : (
                    <>Read more <ChevronDown className="h-3 w-3 ml-1" /></>
                  )}
                </Button>
              )}
              
              {/* Pricing and Date Section - matching service card design */}
              <div className="flex justify-between items-end">
                <div className="flex flex-col">
                  <div className="flex items-center">
                    {request.budget && (
                      <>
                        <span className="font-semibold text-base">{formatBudget(request.budget)}</span>
                        {request.pricing_type === 'hourly' && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/hr</span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Posted {new Date(request.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default RequestList;
